/**
 * Estado COMPLETO da lista de Atendimentos na URL (spec 018, FR-004 / US2
 * AS2): visão, busca, filtros, ordenação e página. Abrir um atendimento e
 * voltar devolve tudo como estava — a rolagem vem do roteador (o id de
 * restauração no shell). Lixo na URL vira o padrão, nunca uma tela quebrada.
 */
import { z } from 'zod';
import { TICKET_SITUATIONS, type TicketListView } from '@erp/shared';

export const VISOES = [
  'minha_fila',
  'precisa_resposta',
  'prazo_em_risco',
  'sem_responsavel',
  'aguardando_cliente',
  'todos',
] as const;
export type Visao = (typeof VISOES)[number];

export const VIEW_OF_VISAO: Record<Visao, TicketListView> = {
  minha_fila: 'mine',
  precisa_resposta: 'needs_reply',
  prazo_em_risco: 'at_risk',
  sem_responsavel: 'unassigned',
  aguardando_cliente: 'waiting_client',
  todos: 'all',
};

const ids = z.array(z.coerce.number().int().positive()).max(15);

export const atendimentosSearchSchema = z.object({
  visao: z.enum(VISOES).default('todos').catch('todos'),
  /** vindo do Hoje em "Minha visão": meus OU sem responsável */
  escopo: z.enum(['meu']).optional().catch(undefined),
  q: z.string().optional().catch(undefined),
  mesas: ids.optional().catch(undefined),
  resp: ids.optional().catch(undefined),
  situacoes: z.array(z.enum(TICKET_SITUATIONS)).max(TICKET_SITUATIONS.length).optional().catch(undefined),
  /** só vale na visão "Todos" — as visões de trabalho olham os abertos */
  status: z.enum(['open', 'closed', 'canceled', 'all']).default('open').catch('open'),
  /** ausente = o padrão da visão (espera do cliente em "Precisa de resposta") */
  ordem: z.enum(['atualizacao', 'criacao', 'espera']).optional().catch(undefined),
  sentido: z.enum(['desc', 'asc']).optional().catch(undefined),
  pagina: z.coerce.number().int().min(1).max(10_000).default(1).catch(1),
});
export type AtendimentosSearch = z.infer<typeof atendimentosSearchSchema>;

export const PAGE_SIZE = 50;

/** Ordenação efetiva: a escolhida, ou a que faz sentido para a visão. */
export function effectiveSort(s: AtendimentosSearch): {
  sort: 'activity' | 'created_at' | 'awaiting';
  order: 'asc' | 'desc';
} {
  const ordem = s.ordem ?? (s.visao === 'precisa_resposta' ? 'espera' : 'atualizacao');
  const sort = ordem === 'criacao' ? 'created_at' : ordem === 'espera' ? 'awaiting' : 'activity';
  // espera: quem espera há mais tempo primeiro
  const order = s.sentido ?? (sort === 'awaiting' ? 'asc' : 'desc');
  return { sort, order };
}

/** Última lista vista NESTE navegador — o "voltar" de quem chegou por link direto. */
const LAST_KEY = 'erp.atendimentos.last';

export function rememberList(search: AtendimentosSearch): void {
  try {
    sessionStorage.setItem(LAST_KEY, JSON.stringify(search));
  } catch {
    /* armazenamento bloqueado: o voltar cai na lista padrão */
  }
}

export function lastList(): AtendimentosSearch {
  try {
    const raw = sessionStorage.getItem(LAST_KEY);
    if (raw) return atendimentosSearchSchema.parse(JSON.parse(raw));
  } catch {
    /* lixo no armazenamento: a lista padrão */
  }
  return atendimentosSearchSchema.parse({});
}
